import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixPremiumDomainsV2Availability from './premium-domains-v2-availability-domain-availability.http.js';

export interface DomainAvailability {
  /**
   * Domain name including TLD. For example, `my-new-domain.com`.
   * @format HOSTNAME
   */
  domain?: string;
  /**
   * Whether the domain is available for purchase. You can purchase the
   * domain in case the boolean is `true`. The domain is already taken
   * when `false` is returned.
   */
  available?: boolean;
  /** Whether the domain has a higher price due to its perceived value or demand. */
  premium?: boolean;
  /**
   * In case the domain is premium the premium_type will represent the type of the premium domain.
   * FIRST_YEAR_ONLY - only initial purchase price is  premium while the renewal will cost the regular price of the TLD.
   * FIRST_YEAR_AND_RENEWAL - both initial purchase and renewal prices are premium.
   */
  premiumType?: PremiumTypeWithLiterals;
}

export enum PremiumType {
  FIRST_YEAR_ONLY = 'FIRST_YEAR_ONLY',
  FIRST_YEAR_AND_RENEWAL = 'FIRST_YEAR_AND_RENEWAL',
}

/** @enumType */
export type PremiumTypeWithLiterals =
  | PremiumType
  | 'FIRST_YEAR_ONLY'
  | 'FIRST_YEAR_AND_RENEWAL';

export interface CheckDomainAvailabilityRequest {
  /**
   * Domain name. Must include the TLD. For example, `my-new-domain.com`. Only
   * alphanumeric characters, hyphens, and dots are supported.
   *
   * Min: 3 characters
   * Max: 63 characters
   * @format HOSTNAME
   */
  domain: string;
}

export interface CheckDomainAvailabilityResponse {
  /** Information about the domain's availability. */
  availability?: DomainAvailability;
}

/**
 * Checks whether the given domain is available for purchase.
 *
 *
 * You can purchase the specified domain in case the returned
 * `availability.available` boolean is `true`. The domain is already taken
 * when `false` is returned.
 *
 * The `domain` field must include the TLD. For example, `my-new-domain.com`.
 *
 * > __Important:__ This call requires an account level API key and cannot be authenticated with the standard authorization header.
 * @param domain - Domain name. Must include the TLD. For example, `my-new-domain.com`. Only
 * alphanumeric characters, hyphens, and dots are supported.
 *
 * Min: 3 characters
 * Max: 63 characters
 * @public
 * @requiredField domain
 * @permissionId DOMAINS.PUBLIC_API_PERMISSION
 * @fqn com.wixpress.premium.domain.search.availability.v2.DomainAvailabilityService.CheckDomainAvailability
 */
export async function checkDomainAvailability(
  domain: string
): Promise<
  NonNullablePaths<
    CheckDomainAvailabilityResponse,
    | `availability.domain`
    | `availability.available`
    | `availability.premium`
    | `availability.premiumType`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ domain: domain });

  const reqOpts =
    ambassadorWixPremiumDomainsV2Availability.checkDomainAvailability(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { domain: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['domain']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
